// Экран, который видно, когда представление не удалось отрисовать вовсе.
//
// ## Зачем он есть
//
// Правило четырёх состояний (`.claude/rules/ui-views.md`) закрывает загрузку, пустоту, ошибку
// запроса и данные. Пятый случай оно не закрывает: исключение внутри самой отрисовки. До
// 25.08.2026 перехватчика не было НИ ОДНОГО, и такое исключение показывало страницу отладки
// маршрутизатора — «Unexpected Application Error!», трассировку стека и обращение к
// разработчику по-английски.
//
// Поймано это было на живом стенде и на настоящей причине: сводка спросила у сервера поле,
// которого он ещё не отдавал, страж денежного формата отказался печатать `NaN` («копейки
// передаются целым числом»), и весь раздел заменился английской трассировкой. Страж сработал
// правильно — деньги обязаны падать, а не показывать мусор, — но человеку от этого достался
// экран, из которого нет выхода ни одним нажатием.
//
// ## Что здесь показывается
//
// То же, что в состоянии ошибки запроса: ЧТО не получилось, техническая строка мелким шрифтом
// (её пересылают снимком, и без неё разбираться не по чему) и два выхода — обновить экран и
// вернуться на стартовый. «Что-то пошло не так» без указания, что пошло, состоянием ошибки у
// нас не считается, и здесь это правило действует тоже.

import { RiRestartLine } from '@remixicon/react';
import { Link, useRouteError } from 'react-router';

import * as Button from '@/shared/ui/button';
import * as LinkButton from '@/shared/ui/link-button';

/** Техническая строка ошибки. Пустая — значит показывать нечего, и мы про это молчим. */
function строкаОшибки(ошибка: unknown): string | null {
  if (ошибка instanceof Error) {
    return ошибка.message;
  }
  if (typeof ошибка === 'string' && ошибка.trim() !== '') {
    return ошибка;
  }
  return null;
}

export function CrashScreen() {
  const ошибка = useRouteError();
  const строка = строкаОшибки(ошибка);

  return (
    <main
      role='alert'
      className='flex h-full flex-1 flex-col items-center justify-center gap-3 bg-bg-weak-50 p-6 text-center'
    >
      <div className='flex max-w-prose flex-col gap-1'>
        <p className='text-label-sm text-text-strong-950'>Экран не удалось показать</p>
        <p className='text-paragraph-sm text-text-sub-600'>
          Программа остановилась на середине отрисовки. Данные при этом не пострадали: ничего не
          сохранялось и не отправлялось.
        </p>
        {строка !== null && (
          <p className='pt-1 text-paragraph-xs text-text-sub-600'>
            Что именно случилось: {строка}
          </p>
        )}
      </div>
      <div className='flex flex-wrap items-center justify-center gap-3'>
        <Button.Root
          variant='neutral'
          mode='stroke'
          size='small'
          onClick={() => window.location.reload()}
        >
          <Button.Icon as={RiRestartLine} />
          Обновить экран
        </Button.Root>
        <LinkButton.Root variant='primary' size='medium' underline asChild>
          <Link to='/'>На стартовый экран</Link>
        </LinkButton.Root>
      </div>
    </main>
  );
}
